import type { QueryKey } from "@tanstack/react-query";
import { getTransactionsSnapshot } from "@api/transaction.api";
import { getSnapshotBatchFilters } from "@common/helpers/transactionIdSnapshot";
import type {
    GetTransactionsResponse,
    GetTransactionsSnapshotResponse,
} from "@common/schemas/transaction.schema";
import {
    convertFiltersToDto,
    loadTransactions,
    TRANSACTIONS_PAGE_SIZE,
    TRANSACTIONS_QUERY_KEY,
    type UseTransactionsQueryParam,
} from "./useTransactionsQuery";

export type SnapshotQueryParams = Pick<
    UseTransactionsQueryParam,
    | "workspaceId"
    | "filters"
    | "sort"
    | "includeMatchDetails"
    | "includeAccrualMatchDetails"
    | "includeAccrualMatchSuggestions"
> & { queryKeyPrefix?: string };

interface SnapshotListQuery<TResponse> {
    queryKey: QueryKey;
    queryFn: () => Promise<TResponse>;
}

const loadTransactionsSnapshot = ({
    filters,
    ...params
}: Omit<
    SnapshotQueryParams,
    "queryKeyPrefix"
>): Promise<GetTransactionsSnapshotResponse> =>
    (filters.entityIds || []).length > 0
        ? getTransactionsSnapshot({
              ...params,
              filters: convertFiltersToDto(filters),
              limit: TRANSACTIONS_PAGE_SIZE,
          })
        : Promise.resolve({ ids: [], isTruncated: false, data: [] });

/**
 * The id snapshot of a list, keyed under the list's prefix so the existing
 * invalidations and cache writers reach it.
 */
export function buildSnapshotQuery({
    queryKeyPrefix,
    ...params
}: SnapshotQueryParams): SnapshotListQuery<GetTransactionsSnapshotResponse> {
    return {
        queryKey: [
            queryKeyPrefix ?? TRANSACTIONS_QUERY_KEY,
            "snapshot",
            params.workspaceId,
            params.filters,
            params.sort,
            params.includeMatchDetails,
            params.includeAccrualMatchDetails,
            params.includeAccrualMatchSuggestions,
        ],
        queryFn: () => loadTransactionsSnapshot(params),
    };
}

/**
 * One later batch of the snapshot, loaded by id regardless of the list's
 * narrowing filters, so a row reviewed since the snapshot still loads in its
 * place.
 */
export function buildSnapshotBatchQuery({
    batchIds,
    queryKeyPrefix,
    ...params
}: SnapshotQueryParams & {
    batchIds: number[];
}): SnapshotListQuery<GetTransactionsResponse> {
    const batchFilters = {
        ...getSnapshotBatchFilters({
            filters: params.filters,
            transactionIds: batchIds,
        }),
        entityIds: params.filters.entityIds,
    };
    return {
        queryKey: [
            queryKeyPrefix ?? TRANSACTIONS_QUERY_KEY,
            "snapshot-batch",
            params.workspaceId,
            params.includeMatchDetails,
            params.includeAccrualMatchDetails,
            params.includeAccrualMatchSuggestions,
            batchFilters,
        ],
        queryFn: () =>
            loadTransactions({
                ...params,
                filters: batchFilters,
                page: 1,
                limit: TRANSACTIONS_PAGE_SIZE,
                skipCount: true,
            }),
    };
}
